import React from 'react';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';
import {
  HeaderSection,
  MainSection,
  SubHeaderSection,
  QuizHeader,
  QuizFooterContainer,
  QuizInnerLayout as BaseQuizInnerLayout,
} from '@skooldio/skooldio-classroom-components';

import { useConfig } from 'Context/ConfigContext';
import QuizWrapper from 'Components/Quiz/Components/QuizWrapper';
import BaseHTMLView from 'Components/HTMLView';

import { Box, Flex, SvgIcon, Text } from '../../../../Base';
import { ActionBox, Radio } from '../../../Components/QuizBox';
import { RESULT_TYPE } from '../constant';
import { QUIZ_VARIANTS } from '../../../constant';

const QuizInnerLayout = styled(BaseQuizInnerLayout)`
  p:first-child {
    margin-top: 0;
  }
  p:last-child {
    margin-bottom: 0;
  }
`;

const TextScore = (props) => <Text textStyle={'subtitle1'} ml={{ _: 2, lg: 4 }} {...props} />;

const HTMLView = (props) => <BaseHTMLView {...props} isShowLoading />;

/**
 * LatexWrapper fixes a MathJax rendering issue in Electron, where mjx-root elements
 * sometimes have a width of 0px, causing LaTeX content to be hidden. This forces
 * a minimal width to ensure proper rendering.
 */
const LatexWrapper = styled(Box)`
  .MathJax mjx-root {
    width: 1px !important; // Ensure LaTeX renders correctly in Electron
  }
`;

const getHeaderText = (variant) => {
  switch (variant) {
    case QUIZ_VARIANTS.OFFICIAL_EXAM:
      return 'เฉลยข้อสอบ';
    default:
      return 'เฉลยแบบฝึกหัด';
  }
};

const getResultType = (resultType) => {
  switch (resultType) {
    case RESULT_TYPE.INCORRECT:
      return {
        bg: 'dangerLighter',
        icon: SvgIcon.ICON_LIST.CLOSE_CIRCLE,
        color: 'danger',
        text: 'ผิด',
      };
    case RESULT_TYPE.CORRECT:
    default:
      return {
        bg: 'successLighter',
        icon: SvgIcon.ICON_LIST.CHECK_CIRCLE,
        color: 'success',
        text: 'ถูก',
      };
  }
};

const TitleBar = ({ questionNo, result, isShowScore, score }) => {
  const resultType = getResultType(result);

  return (
    <Flex
      height={56}
      justifyContent={'center'}
      alignItems={'center'}
      bg={resultType.bg}
      width={'100%'}
    >
      <Flex
        justifyContent={'space-between'}
        alignItems={'center'}
        px={6}
        width={'100%'}
        maxWidth={1164}
      >
        <Text textStyle={'subtitle1'}>{`ข้อ ${questionNo}`}</Text>
        <Flex alignItems={'center'}>
          <SvgIcon icon={resultType.icon} color={resultType.color} size={24} mr={2} />
          <Text textStyle={'subtitle1'}>{resultType.text}</Text>
          {isShowScore && <TextScore>{score.toFixed(2)} คะแนน</TextScore>}
        </Flex>
      </Flex>
    </Flex>
  );
};

/**
 * create choices component
 * @param {Object} props
 * @param {{value: string, label: string, result: (keyof RESULT_TYPE)}[] | null} props.selectedAnswer
 */
const SelectedChoice = ({ selectedAnswer }) => {
  if (!selectedAnswer) {
    return <Radio clickable={false} variant={Radio.VARIANT.DEFAULT} label={'ไม่ได้เลือกคำตอบ'} />;
  }

  return selectedAnswer.map((answer, index) => {
    /**
     * Wrap the label with LatexWrapper to ensure proper LaTeX rendering.
     * This workaround is needed for cases where the first character isn't a
     * simple letter or number, which can cause rendering issues in Electron.
     */
    return (
      <LatexWrapper key={`${answer.value + '_' + index}`}>
        <Radio
          key={`${answer.value + '_radio_' + index}`}
          clickable={false}
          variant={
            answer.result === RESULT_TYPE.CORRECT ? Radio.VARIANT.CORRECT : Radio.VARIANT.INCORRECT
          }
          label={answer.label}
          mb={index < selectedAnswer.length - 1 ? 2 : 0}
        />
      </LatexWrapper>
    );
  });
};

const SolutionDetail = ({
  variant,
  result,
  solutionContent,
  onBack,
  onNextQuestion,
  onPreviousQuestion,
  isLastQuestion,
  isFirstQuestion,
  selectedAnswer,
  questionNo,
  questionContent,
  isShowScore,
  score,
}) => {
  const {
    classroom: { quiz = {} },
  } = useConfig();
  const { bgColor = 'primary' } = quiz;
  const { colors } = useTheme();
  return (
    <QuizWrapper subHeader>
      <HeaderSection>
        <QuizHeader
          title={getHeaderText(variant)}
          backIcon={true}
          onBack={onBack}
          outerWrapperProps={{ bg: colors[bgColor] }}
        />
      </HeaderSection>
      <SubHeaderSection>
        <TitleBar questionNo={questionNo} result={result} isShowScore={isShowScore} score={score} />
      </SubHeaderSection>
      <MainSection>
        <QuizInnerLayout>
          <Box>
            {questionContent && (
              <Box pt={6}>
                <Text textStyle={'subtitle1'} mb={3}>
                  คำถาม
                </Text>
                <LatexWrapper>
                  <HTMLView rawContent={questionContent} />
                </LatexWrapper>
              </Box>
            )}
            <Text textStyle={'subtitle1'} pt={6} mb={3}>
              คำตอบของคุณ
            </Text>
            <SelectedChoice selectedAnswer={selectedAnswer} />
            <Text textStyle={'subtitle1'} mt={6} mb={3}>
              เฉลยละเอียด
            </Text>
            <LatexWrapper>
              <HTMLView rawContent={solutionContent} />
            </LatexWrapper>
          </Box>
          <QuizFooterContainer>
            <ActionBox
              style={{ height: '100%' }}
              onNext={onNextQuestion}
              onBack={onPreviousQuestion}
              isLastQuestion={isLastQuestion}
              isFirstQuestion={isFirstQuestion}
              nextButtonText={isLastQuestion ? 'ออก' : 'ข้อถัดไป'}
            />
          </QuizFooterContainer>
        </QuizInnerLayout>
      </MainSection>
    </QuizWrapper>
  );
};

SolutionDetail.propTypes = {
  solutionContent: PropTypes.string,
  result: PropTypes.oneOf(Object.values(RESULT_TYPE)),
  onBack: PropTypes.func,
  onNextQuestion: PropTypes.func,
  onPreviousQuestion: PropTypes.func,
  isFirstQuestion: PropTypes.bool,
  isLastQuestion: PropTypes.bool,
  questionNo: PropTypes.number,
};

SolutionDetail.defaultProps = {
  onBack: () => {},
  onNextQuestion: () => {},
  onPreviousQuestion: () => {},
};

export { SelectedChoice, getHeaderText, TitleBar, LatexWrapper, HTMLView, QuizInnerLayout };
export default SolutionDetail;
